import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Store, BadgeCheck, Plus, Pencil, Trash2, X, Save, CalendarDays, BookOpen, Shield, UserCog, Info, UserPlus, PackagePlus } from "lucide-react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import PhotoUpload from "@/components/PhotoUpload";
import Modal from "@/components/Modal";
import MemberFormModal from "@/components/MemberFormModal";
import BusinessFormModal from "@/components/BusinessFormModal";

export default function AdminDashboard() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const readOnly = !isAdmin;
  const canEditEvents = currentUser?.role === "admin" || currentUser?.role === "pengurus";
  const canAddPeople = canEditEvents;
  const [memberDraft, setMemberDraft] = useState(null);
  const [businessDraft, setBusinessDraft] = useState(null);

  const openNewMember = () => setMemberDraft({});
  const saveMember = async (m) => {
    if (!m.name || !m.email || !m.password || !m.whatsapp || !m.address) { toast.error("Nama, email, password, WA, alamat wajib"); return; }
    if (m.password.length < 8) { toast.error("Password minimal 8 karakter"); return; }
    try {
      await api.post("/admin/members", m);
      toast.success("Anggota berhasil dibuat. Anggota dapat login dan diminta verifikasi email.");
      setMemberDraft(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const openNewBusiness = () => setBusinessDraft({});
  const saveBusiness = async (b) => {
    try {
      await api.post("/admin/businesses", b);
      toast.success("Bisnis berhasil dibuat");
      setBusinessDraft(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [q, setQ] = useState("");
  const [bizQ, setBizQ] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [editingCat, setEditingCat] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventDraft, setEventDraft] = useState(null);

  const load = async () => {
    const [s, m, c, e, b] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/members"),
      api.get("/categories"), api.get("/events"),
      api.get("/businesses"),
    ]);
    setStats(s.data); setMembers(m.data); setCategories(c.data); setEvents(e.data); setBusinesses(b.data);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/members/${u.user_id}/active`, { is_active: !u.is_active });
      toast.success(!u.is_active ? "Anggota diaktifkan" : "Anggota dinonaktifkan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const toggleVerified = async (u) => {
    try {
      await api.patch(`/admin/members/${u.user_id}/verify`, { is_verified: !u.is_verified });
      toast.success(!u.is_verified ? "Anggota diverifikasi" : "Verifikasi dicabut");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const deleteMember = async (u) => {
    if (!window.confirm(`Hapus permanen anggota "${u.name}" dan semua bisnisnya?`)) return;
    try {
      const { data } = await api.delete(`/admin/members/${u.user_id}`);
      toast.success(`Anggota dihapus (${data.deleted_businesses} bisnis ikut terhapus)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const deleteBusiness = async (b) => {
    if (!window.confirm(`Hapus bisnis "${b.name}"?`)) return;
    try { await api.delete(`/businesses/${b.business_id}`); toast.success("Bisnis dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const makeAdmin = async (u) => {
    const promote = u.role !== "admin";
    if (!window.confirm(promote ? `Jadikan "${u.name}" sebagai admin?` : `Turunkan "${u.name}" ke anggota biasa?`)) return;
    try { await api.patch(`/admin/members/${u.user_id}/role`, { role: promote ? "admin" : "member" }); toast.success(promote?"Anggota dijadikan admin":"Admin diturunkan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const togglePengurus = async (u) => {
    if (u.role === "admin") return;
    const isPengurus = u.role === "pengurus";
    if (!window.confirm(isPengurus ? `Cabut status pengurus dari "${u.name}"?` : `Jadikan "${u.name}" sebagai pengurus?`)) return;
    try { await api.patch(`/admin/members/${u.user_id}/role`, { role: isPengurus ? "member" : "pengurus" }); toast.success(isPengurus?"Status pengurus dicabut":"Anggota dijadikan pengurus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try { await api.post("/admin/categories", { name: newCat.trim() }); setNewCat(""); toast.success("Kategori ditambah"); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Gagal"); }
  };
  const saveCategory = async (c) => {
    try { await api.patch(`/admin/categories/${c.category_id}`, { name: c.name }); setEditingCat(null); toast.success("Kategori diperbarui"); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Gagal"); }
  };
  const removeCategory = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"?`)) return;
    try { await api.delete(`/admin/categories/${c.category_id}`); toast.success("Kategori dihapus"); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Gagal"); }
  };

  const openNewEvent = () => setEventDraft({ title: "", description: "", date: "", location: "", photo_path: "", is_pengurus_only: false });
  const saveEvent = async () => {
    if (!eventDraft.title || !eventDraft.date || !eventDraft.description) { toast.error("Judul, tanggal, deskripsi wajib"); return; }
    try {
      const iso = new Date(eventDraft.date).toISOString();
      const payload = { title: eventDraft.title, description: eventDraft.description, date: iso, location: eventDraft.location, photo_path: eventDraft.photo_path, is_pengurus_only: !!eventDraft.is_pengurus_only };
      if (eventDraft.event_id) await api.patch(`/admin/events/${eventDraft.event_id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(eventDraft.event_id ? "Event diperbarui" : "Event dibuat");
      setEventDraft(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const removeEvent = async (ev) => {
    if (!window.confirm(`Hapus event "${ev.title}"?`)) return;
    try { await api.delete(`/admin/events/${ev.event_id}`); toast.success("Event dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const filtered = members.filter(m =>
    !q || m.name?.toLowerCase().includes(q.toLowerCase()) || m.email?.toLowerCase().includes(q.toLowerCase())
  );
  const filteredBiz = businesses.filter(b =>
    !bizQ || b.name?.toLowerCase().includes(bizQ.toLowerCase()) || b.category?.toLowerCase().includes(bizQ.toLowerCase()) || b.owner_name?.toLowerCase().includes(bizQ.toLowerCase())
  );

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">Admin Bizma</div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold mt-1">Dashboard</h1>
        <p className="text-bizma-subtext mt-2">Statistik & manajemen anggota komunitas.</p>
        {readOnly && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-2xl bg-bizma-sage/10 border border-bizma-sage/30 text-sm text-bizma-sage" data-testid="pengurus-readonly-banner">
            <Info size={16} className="mt-0.5 shrink-0"/>
            <div>Mode <b>Pengurus</b> — kamu bisa melihat semua data tapi tidak bisa mengubah, menambah, atau menghapus.</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="admin-stats">
        <StatCard icon={<Users/>} label="Total Anggota" value={stats?.total_users ?? "-"} sub={`${stats?.active_users ?? 0} aktif`} testId="stat-users" to="/app" />
        <StatCard icon={<Store/>} label="Total Bisnis" value={stats?.total_businesses ?? "-"} sub="Bisnis terdaftar" testId="stat-businesses" to="/app?tab=businesses" />
        <StatCard icon={<CalendarDays/>} label="Total Event" value={stats?.total_events ?? "-"} sub="Meetup komunitas" testId="stat-events" to="/app/events" />
        <StatCard icon={<BookOpen/>} label="Total Blog" value={stats?.total_blogs ?? "-"} sub="Artikel dibagikan" testId="stat-blogs" to="/app/blog" />
      </div>

      <div className="felt-card mt-6 p-6" data-testid="signup-chart-card">
        <h2 className="font-display font-semibold text-lg">Pendaftar Harian — {stats?.month}</h2>
        <div className="h-72 mt-4">
          {stats && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.daily_signups} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C85A40" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C85A40" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                <XAxis dataKey="day" tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E0D8", borderRadius: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#C85A40" strokeWidth={3} fill="url(#grad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="felt-card mt-6 p-6" data-testid="admin-members-panel">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <h2 className="font-display font-semibold text-lg flex-1">Manajemen Anggota</h2>
          <button onClick={openNewMember} disabled={!canAddPeople} className="btn-primary text-sm py-2 px-4 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-member-btn"><UserPlus size={14}/> Tambah Anggota</button>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau email..." className="input-bizma sm:w-72" data-testid="admin-search" />
        </div>
        {/* Mobile card list */}
        <div className="sm:hidden space-y-3">
          {filtered.map(u => (
            <div key={u.user_id} className="border border-bizma-border rounded-2xl p-4 bg-white" data-testid={`admin-row-${u.user_id}`}>
              <div className="flex items-start gap-3">
                {u.photo_path ? <img src={fileUrl(u.photo_path)} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" /> :
                  <div className="w-11 h-11 rounded-full bg-bizma-sage text-white grid place-items-center font-semibold shrink-0">{u.name?.charAt(0)}</div>}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-xs text-bizma-subtext truncate">{u.email}</div>
                  <div className="mt-1 flex items-center flex-wrap gap-1.5">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${u.role === "admin" ? "bg-bizma-terracotta/10 text-bizma-terracotta" : u.role === "pengurus" ? "bg-bizma-sage/15 text-bizma-sage" : "bg-bizma-muted text-bizma-ink"}`}>{u.role}</span>
                    <span className={`text-[10px] ${u.is_active ? "text-bizma-sage" : "text-red-500"} font-semibold`}>{u.is_active ? "● Aktif" : "● Nonaktif"}</span>
                    {u.event_count > 0 && <span className="inline-flex items-center gap-1 text-[10px] text-bizma-subtext"><CalendarDays size={10}/> {u.event_count}</span>}
                  </div>
                </div>
                <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} disabled={u.role === "admin" || readOnly} data-testid={`toggle-active-${u.user_id}`} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => toggleVerified(u)} disabled={readOnly}
                  className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-colors ${u.is_verified ? "bg-bizma-sage text-white" : "bg-bizma-muted text-bizma-subtext"} disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`toggle-verify-${u.user_id}`}>
                  <BadgeCheck size={12}/> {u.is_verified ? "Verified" : "Verifikasi"}
                </button>
                <button onClick={() => togglePengurus(u)} disabled={u.role === "admin" || readOnly}
                  className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-colors ${u.role === "pengurus" ? "bg-bizma-sage/15 text-bizma-sage" : "text-bizma-sage border border-bizma-sage/40"} disabled:opacity-30 disabled:cursor-not-allowed`}
                  data-testid={`toggle-pengurus-${u.user_id}`}>
                  <UserCog size={12}/> Pengurus
                </button>
                <button onClick={() => makeAdmin(u)} disabled={u.email === "nurrohmat.m@gmail.com" || readOnly}
                  className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-colors ${u.role === "admin" ? "bg-bizma-terracotta/10 text-bizma-terracotta" : "text-bizma-sage border border-bizma-sage/40"} disabled:opacity-30 disabled:cursor-not-allowed`}
                  data-testid={`make-admin-${u.user_id}`}>
                  <Shield size={12}/> Admin
                </button>
                <button onClick={() => deleteMember(u)} disabled={u.role === "admin" || readOnly}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-red-600 border border-red-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`delete-member-${u.user_id}`}>
                  <Trash2 size={12}/> Hapus
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-8 text-bizma-subtext text-sm">Tidak ada anggota</div>}
        </div>

        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-bizma-subtext border-b border-bizma-border">
                <th className="py-3 px-2">Anggota</th>
                <th className="py-3 px-2 hidden md:table-cell">Email</th>
                <th className="py-3 px-2 hidden md:table-cell">Role</th>
                <th className="py-3 px-2 hidden lg:table-cell">Tgl Daftar</th>
                <th className="py-3 px-2 text-center">Event</th>
                <th className="py-3 px-2 text-center">Verified</th>
                <th className="py-3 px-2 text-center">Status</th>
                <th className="py-3 px-2 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.user_id} className="border-b border-bizma-border/60" data-testid={`admin-row-desk-${u.user_id}`}>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-3">
                      {u.photo_path ? <img src={fileUrl(u.photo_path)} alt="" className="w-9 h-9 rounded-full object-cover" /> :
                        <div className="w-9 h-9 rounded-full bg-bizma-sage text-white grid place-items-center font-semibold text-sm">{u.name?.charAt(0)}</div>}
                      <div>
                        <div className="font-medium">{u.name}</div>
                        <div className="text-xs text-bizma-subtext md:hidden">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-2 hidden md:table-cell">{u.email}</td>
                  <td className="py-3 px-2 hidden md:table-cell">
                    <span className={`text-xs px-2 py-1 rounded-full ${u.role === "admin" ? "bg-bizma-terracotta/10 text-bizma-terracotta" : u.role === "pengurus" ? "bg-bizma-sage/15 text-bizma-sage" : "bg-bizma-muted text-bizma-ink"}`}>{u.role}</span>
                  </td>
                  <td className="py-3 px-2 hidden lg:table-cell text-bizma-subtext">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("id-ID") : "-"}
                  </td>
                  <td className="py-3 px-2 text-center" data-testid={`event-count-${u.user_id}`}>
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-bizma-cream border border-bizma-border"><CalendarDays size={11}/> {u.event_count || 0}</span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <button onClick={() => toggleVerified(u)} disabled={readOnly}
                      className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full transition-colors ${u.is_verified ? "bg-bizma-sage text-white" : "bg-bizma-muted text-bizma-subtext hover:bg-bizma-border"} disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={readOnly ? "Hanya admin yang bisa mengubah verifikasi" : "Toggle verifikasi anggota (badge biru)"}
                      data-testid={`toggle-verify-desk-${u.user_id}`}>
                      <BadgeCheck size={12}/> {u.is_verified ? "Verified" : "Verifikasi"}
                    </button>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <div className="inline-flex items-center gap-2">
                      <span className={`text-xs ${u.is_active ? "text-bizma-sage" : "text-red-500"}`}>{u.is_active ? "Aktif" : "Nonaktif"}</span>
                      <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} disabled={u.role === "admin" || readOnly} data-testid={`toggle-active-desk-${u.user_id}`} />
                    </div>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <button onClick={() => togglePengurus(u)} disabled={u.role === "admin" || readOnly}
                        className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-colors ${u.role === "pengurus" ? "bg-bizma-sage/15 text-bizma-sage" : "text-bizma-sage border border-bizma-sage/40 hover:bg-bizma-sage/10"} disabled:opacity-30 disabled:cursor-not-allowed`}
                        data-testid={`toggle-pengurus-desk-${u.user_id}`}>
                        <UserCog size={12}/> Pengurus
                      </button>
                      <button onClick={() => makeAdmin(u)} disabled={u.email === "nurrohmat.m@gmail.com" || readOnly}
                        className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-colors ${u.role === "admin" ? "bg-bizma-terracotta/10 text-bizma-terracotta" : "text-bizma-sage border border-bizma-sage/40 hover:bg-bizma-sage/10"} disabled:opacity-30 disabled:cursor-not-allowed`}
                        data-testid={`make-admin-desk-${u.user_id}`}>
                        <Shield size={12}/> Admin
                      </button>
                      <button onClick={() => deleteMember(u)} disabled={u.role === "admin" || readOnly}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-red-600 border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`delete-member-desk-${u.user_id}`}>
                        <Trash2 size={12}/> Hapus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-bizma-subtext">Tidak ada anggota</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Business Management */}
      <div className="felt-card mt-6 p-6" data-testid="admin-businesses-panel">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <h2 className="font-display font-semibold text-lg flex-1 flex items-center gap-2"><Store size={18}/> Manajemen Bisnis</h2>
          <button onClick={openNewBusiness} disabled={!canAddPeople} className="btn-primary text-sm py-2 px-4 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-business-btn"><PackagePlus size={14}/> Tambah Bisnis</button>
          <input value={bizQ} onChange={(e) => setBizQ(e.target.value)} placeholder="Cari nama, kategori, atau pemilik..." className="input-bizma sm:w-80" data-testid="admin-biz-search" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBiz.map((b) => (
            <div key={b.business_id} className="border border-bizma-border rounded-2xl p-4" data-testid={`admin-biz-${b.business_id}`}>
              <Link to={`/app/businesses/${b.business_id}`} className="block">
                <div className="w-full h-28 rounded-xl bg-bizma-muted overflow-hidden">
                  {b.photo_path && <img src={fileUrl(b.photo_path)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold mt-2">{b.category}</div>
                <div className="font-display font-semibold mt-1 truncate">{b.name}</div>
                <div className="text-xs text-bizma-subtext mt-1">oleh {b.owner_name}</div>
              </Link>
              <button onClick={() => deleteBusiness(b)} disabled={readOnly}
                className="mt-3 w-full inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-full text-red-600 border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`admin-delete-biz-${b.business_id}`}>
                <Trash2 size={12}/> Hapus Bisnis
              </button>
            </div>
          ))}
          {filteredBiz.length === 0 && <div className="col-span-full text-center py-8 text-bizma-subtext">Tidak ada bisnis.</div>}
        </div>
      </div>

      {/* Category Management */}
      <div className="felt-card mt-6 p-6" data-testid="admin-categories-panel">
        <h2 className="font-display font-semibold text-lg mb-4">Manajemen Kategori</h2>
        <form onSubmit={addCategory} className="flex flex-col sm:flex-row gap-3 mb-5">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} disabled={readOnly}
            placeholder="Nama kategori baru (contoh: Otomotif)"
            className="input-bizma flex-1 disabled:bg-bizma-muted disabled:cursor-not-allowed" data-testid="new-category-input" />
          <button disabled={readOnly} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-category-btn"><Plus size={16}/> Tambah</button>
        </form>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories.map((c) => (
            <div key={c.category_id} className="flex items-center gap-2 border border-bizma-border rounded-2xl px-4 py-2" data-testid={`category-row-${c.category_id}`}>
              {editingCat?.category_id === c.category_id ? (
                <>
                  <input value={editingCat.name} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })}
                    className="input-bizma h-10 flex-1" data-testid={`edit-category-input-${c.category_id}`} />
                  <button onClick={() => saveCategory(editingCat)} className="text-bizma-sage p-2" data-testid={`save-category-${c.category_id}`}><Save size={16}/></button>
                  <button onClick={() => setEditingCat(null)} className="text-bizma-subtext p-2"><X size={16}/></button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-medium">{c.name}</span>
                  <button onClick={() => setEditingCat({ ...c })} disabled={readOnly} className="text-bizma-subtext hover:text-bizma-terracotta p-2 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`edit-category-${c.category_id}`}><Pencil size={14}/></button>
                  <button onClick={() => removeCategory(c)} disabled={readOnly} className="text-red-500 hover:text-red-700 p-2 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`delete-category-${c.category_id}`}><Trash2 size={14}/></button>
                </>
              )}
            </div>
          ))}
          {categories.length === 0 && <div className="col-span-full text-sm text-bizma-subtext">Belum ada kategori.</div>}
        </div>
      </div>

      {/* Event Management */}
      <div className="felt-card mt-6 p-6" data-testid="admin-events-panel">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><CalendarDays size={18}/> Event Meetup</h2>
          <button onClick={openNewEvent} disabled={!canEditEvents} className="btn-primary text-sm py-2 px-4 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-event-btn"><Plus size={14}/> Tambah</button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((e) => (
            <div key={e.event_id} className="border border-bizma-border rounded-2xl p-4" data-testid={`admin-event-${e.event_id}`}>
              {e.photo_path && <img src={fileUrl(e.photo_path)} alt="" className="w-full h-24 object-cover rounded-xl mb-3" />}
              <div className="text-xs text-bizma-sage font-bold">{new Date(e.date).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</div>
              <div className="font-display font-semibold mt-1 flex items-center gap-2 flex-wrap">
                {e.title}
                {e.is_pengurus_only && <span className="text-[10px] uppercase tracking-widest bg-bizma-terracotta/10 text-bizma-terracotta font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Shield size={10}/> Pengurus</span>}
              </div>
              <div className="text-xs text-bizma-subtext line-clamp-2 mt-1">{e.description}</div>
              <div className="mt-3 flex items-center gap-2">
                <button onClick={() => setEventDraft({ ...e, date: new Date(e.date).toISOString().slice(0, 16), is_pengurus_only: !!e.is_pengurus_only })}
                  disabled={!canEditEvents}
                  className="btn-outline text-xs py-1.5 px-3 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`edit-event-${e.event_id}`}><Pencil size={12}/> Edit</button>
                <button onClick={() => removeEvent(e)}
                  disabled={!canEditEvents}
                  className="btn-outline text-xs py-1.5 px-3 text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`delete-event-${e.event_id}`}><Trash2 size={12}/> Hapus</button>
              </div>
            </div>
          ))}
          {events.length === 0 && <div className="col-span-full text-sm text-bizma-subtext">Belum ada event. Klik "Tambah" untuk membuat event pertama.</div>}
        </div>

        <MemberFormModal open={!!memberDraft} onClose={() => setMemberDraft(null)} onSubmit={saveMember} isAdmin={isAdmin} />
        <BusinessFormModal open={!!businessDraft} onClose={() => setBusinessDraft(null)} onSubmit={saveBusiness} />


        {eventDraft && (
          <Modal
            open={!!eventDraft}
            onClose={() => setEventDraft(null)}
            title={eventDraft.event_id ? "Edit Event" : "Event Baru"}
            testId="event-modal"
            footer={
              <>
                <button type="button" onClick={() => setEventDraft(null)} className="btn-outline flex-1" data-testid="cancel-event-btn">Batalkan</button>
                <button onClick={saveEvent} className="btn-primary flex-1" data-testid="save-event-btn"><Save size={16}/> {eventDraft.event_id ? "Simpan" : "Buat"}</button>
              </>
            }
          >
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Foto Sampul (opsional)</label>
                <div className="mt-2"><PhotoUpload value={eventDraft.photo_path} onChange={(p) => setEventDraft({ ...eventDraft, photo_path: p })} shape="square" testId="event-photo" /></div>
              </div>
              <div><label className="text-sm font-medium">Judul</label>
                <input value={eventDraft.title} onChange={(e) => setEventDraft({ ...eventDraft, title: e.target.value })} className="input-bizma mt-2" data-testid="event-title" /></div>
              <div><label className="text-sm font-medium">Tanggal & Waktu</label>
                <input type="datetime-local" value={eventDraft.date} onChange={(e) => setEventDraft({ ...eventDraft, date: e.target.value })} className="input-bizma mt-2" data-testid="event-date" /></div>
              <div><label className="text-sm font-medium">Lokasi</label>
                <input value={eventDraft.location || ""} onChange={(e) => setEventDraft({ ...eventDraft, location: e.target.value })} className="input-bizma mt-2" placeholder="Kafe / Aula / Alamat" data-testid="event-location" /></div>
              <div><label className="text-sm font-medium">Deskripsi</label>
                <textarea rows={4} value={eventDraft.description} onChange={(e) => setEventDraft({ ...eventDraft, description: e.target.value })} className="input-bizma h-auto py-3 mt-2" data-testid="event-description" /></div>
              <label className="flex items-start gap-3 p-3 rounded-2xl border border-bizma-border hover:bg-bizma-muted cursor-pointer" data-testid="event-pengurus-toggle-wrap">
                <input type="checkbox" checked={!!eventDraft.is_pengurus_only}
                  onChange={(e) => setEventDraft({ ...eventDraft, is_pengurus_only: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-bizma-terracotta" data-testid="event-pengurus-toggle" />
                <div>
                  <div className="text-sm font-medium flex items-center gap-1"><Shield size={12}/> Kopdar Pengurus</div>
                  <div className="text-xs text-bizma-subtext mt-0.5">Hanya admin & pengurus yang bisa melihat & RSVP event ini.</div>
                </div>
              </label>
            </div>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ icon, label, value, sub, testId, to }) {
  const inner = (
    <>
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-bizma-terracotta/10 text-bizma-terracotta grid place-items-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-bizma-sage font-bold truncate">{label}</div>
        <div className="text-2xl sm:text-3xl font-display font-bold mt-0.5 sm:mt-1 truncate">{value}</div>
        <div className="text-xs sm:text-sm text-bizma-subtext mt-0.5 sm:mt-1 truncate">{sub}</div>
      </div>
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testId} className="felt-card p-3 sm:p-6 flex items-start gap-3 sm:gap-4 min-w-0 transition-transform hover:-translate-y-1 hover:shadow-md cursor-pointer">
        {inner}
      </Link>
    );
  }
  return (
    <div className="felt-card p-3 sm:p-6 flex items-start gap-3 sm:gap-4 min-w-0" data-testid={testId}>
      {inner}
    </div>
  );
}

